/**
 * /parity/uo_user — 线网同构：`admin/uo.user/index`（Layui：Tab + 行内只读 + 右对齐 label）
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, ConfigProvider, Form, Input, Modal, Select, Tabs, theme } from 'antd';
import type { CSSProperties } from 'react';
import { useAuthStore } from '../../store/auth';
import { api, ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { TOKEN_META } from '@uo/shared';

type LegacySnap = {
  rp: string;
  tp: string;
  ep: string;
  ep_to_uoc: string;
  mirror_imported_at: string | null;
  has_mirror: boolean;
};

type LogRow = {
  id: string;
  user_id: number;
  asset: string;
  delta: string;
  note: string | null;
  created_at: string;
};

const readRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  marginBottom: 12,
  padding: '10px 12px',
  background: '#f5f5f5',
  border: '1px solid #e8e8e8',
  borderRadius: 2,
  fontSize: 14,
};

const formItemMb = 18;

export function ParityUoUserPage() {
  const { message: msg } = App.useApp();
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const hasMenu = useAuthStore((s) => s.hasMenu);
  const isSuper = useAuthStore((s) => s.profile?.is_super === true);
  const canUse =
    isSuper ||
    hasMenu('parity.uo_user') ||
    hasNode('parity.uo_user') ||
    hasNode('platform.user.transfer_coin') ||
    hasMenu('platform.transfer_coin');

  const { data: snap, isLoading, refetch: refetchSnap } = useQuery({
    queryKey: ['parity-uo-user', 'legacy-php-form'],
    queryFn: () => api.get<LegacySnap>('/api/admin/parity-uo-user/legacy-php-form'),
    enabled: canUse,
  });

  const [aForm] = Form.useForm();
  const [uForm] = Form.useForm();
  const { token } = theme.useToken();

  const aMut = useMutation({
    mutationFn: (v: { account: string; num: string; remark: string; xian: boolean }) =>
      api.post<unknown>('/api/admin/parity-uo-user/activation-to', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-transfer-coin-log'] });
      void refetchSnap();
      msg.success('发放成功（TP）');
      aForm.resetFields(['num', 'remark', 'account']);
    },
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '失败'),
  });

  const uMut = useMutation({
    mutationFn: (v: { account: string; num: string; remark: string; coin: 'rp' | 'tp'; xian: boolean }) =>
      api.post<unknown>('/api/admin/parity-uo-user/uoc-to', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-transfer-coin-log'] });
      void refetchSnap();
      msg.success('发放成功');
      uForm.resetFields(['num', 'remark', 'account']);
    },
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '失败'),
  });

  const onGiveActivation = () => {
    aForm
      .validateFields()
      .then((v) => {
        Modal.confirm({
          title: '确定操作吗？',
          onOk: () => {
            aMut.mutate({
              account: v.account,
              num: String(v.num).trim(),
              remark: String(v.remark).trim(),
              xian: false,
            });
          },
        });
      })
      .catch(() => {});
  };

  const onGiveUoc = () => {
    uForm
      .validateFields()
      .then((v) => {
        Modal.confirm({
          title: '确定操作吗？',
          onOk: () => {
            uMut.mutate({
              account: v.account,
              num: String(v.num).trim(),
              remark: String(v.remark).trim(),
              coin: v.coin,
              xian: false,
            });
          },
        });
      })
      .catch(() => {});
  };

  const logColumns = [
    { title: '金额', dataIndex: 'delta', width: 120 },
    { title: '备注', dataIndex: 'note', width: 300, ellipsis: true },
    {
      title: '资产',
      dataIndex: 'asset',
      width: 72,
      render: (v: string) => (TOKEN_META as Record<string, { symbol: string }>)[v]?.symbol ?? v,
    },
    {
      title: '发放时间',
      dataIndex: 'created_at',
      width: 180,
      render: (t: string) => new Date(t).toLocaleString('zh-CN'),
    },
  ];

  if (!canUse) {
    return <p style={{ color: '#999' }}>无权限：需要 parity.uo_user 或 platform.user.transfer_coin</p>;
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Tabs: {
            inkBarColor: '#16a34a',
            itemActiveColor: '#16a34a',
            itemColor: 'rgba(0,0,0,0.65)',
            itemHoverColor: '#16a34a',
            itemSelectedColor: '#16a34a',
            titleFontSize: 15,
            horizontalMargin: '0 0 0 0',
            horizontalItemPadding: '10px 16px 12px',
          },
        },
      }}
    >
      <div className="parity-uo-user-page" style={{ background: '#fff', minHeight: 360, padding: '0 0 8px' }}>
        <style>{`
          .parity-uo-user-page .ant-tabs-top > .ant-tabs-nav { margin-bottom: 16px; }
          .parity-uo-user-page .ant-tabs-tab-active .ant-tabs-tab-btn { font-weight: 600; }
        `}</style>
        {snap && !snap.has_mirror && !isLoading && (
          <div
            style={{
              marginBottom: 12,
              padding: 10,
              background: token.colorInfoBg,
              border: `1px solid ${token.colorBorderSecondary}`,
              fontSize: 12,
            }}
          >
            无 php_uo_uo_config 镜像时池子显示为 0。导入镜像后刷新页面可同步与线网一致的池子只读数。
          </div>
        )}

        <Tabs
          type="line"
          defaultActiveKey="a1"
          items={[
            {
              key: 'a1',
              label: '给用户转币',
              children: (
                <div style={{ maxWidth: 900 }}>
                  {isLoading ? null : (
                    <>
                      <div style={readRowStyle}>
                        <span style={{ width: 200, textAlign: 'right', color: 'rgba(0,0,0,0.85)' }}>RP</span>
                        <span style={{ marginLeft: 8, flex: 1, fontWeight: 500, fontFamily: 'inherit' }}>{snap?.rp ?? '—'}</span>
                      </div>
                      <div style={readRowStyle}>
                        <span style={{ width: 200, textAlign: 'right' }}>TP</span>
                        <span style={{ marginLeft: 8, flex: 1, fontWeight: 500 }}>{snap?.tp ?? '—'}</span>
                      </div>
                      <div style={readRowStyle}>
                        <span style={{ width: 200, textAlign: 'right' }}>EP</span>
                        <span style={{ marginLeft: 8, flex: 1, fontWeight: 500 }}>{snap?.ep ?? '—'}</span>
                      </div>
                    </>
                  )}
                  <Form
                    form={aForm}
                    layout="horizontal"
                    labelAlign="right"
                    colon={false}
                    requiredMark
                    style={{ maxWidth: 900 }}
                    labelCol={{ flex: '0 0 200px' }}
                    wrapperCol={{ flex: '1 1 auto' }}
                  >
                    <Form.Item label="转换币种" style={{ marginBottom: formItemMb }}>
                      <div style={{ padding: '4px 0' }}>TP</div>
                    </Form.Item>
                    <Form.Item
                      name="num"
                      label="发放数量"
                      rules={[{ required: true, message: '请输入发放数量' }]}
                      style={{ marginBottom: formItemMb }}
                    >
                      <Input
                        size="large"
                        placeholder="发放数量"
                        allowClear
                        style={{ maxWidth: '100%' }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="account"
                      label="用户账号(登录名)"
                      rules={[{ required: true, message: '请输入登录名' }]}
                      style={{ marginBottom: formItemMb }}
                    >
                      <Input
                        size="large"
                        placeholder="用户登录名"
                        autoComplete="off"
                        style={{ maxWidth: '100%' }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="remark"
                      label="备注"
                      rules={[{ required: true, message: '请输入备注' }]}
                      style={{ marginBottom: 22 }}
                    >
                      <Input size="large" placeholder="备注" style={{ maxWidth: '100%' }} />
                    </Form.Item>
                    <div style={{ position: 'relative', minHeight: 44, marginTop: 12 }}>
                      <Button
                        type="primary"
                        size="small"
                        style={{ position: 'absolute', left: 0, bottom: 0 }}
                        onClick={() => {
                          void refetchSnap();
                          void qc.invalidateQueries({ queryKey: ['admin-transfer-coin-log'] });
                          msg.info('已刷新数据');
                        }}
                      >
                        刷新页面
                      </Button>
                      <div style={{ display: 'flex', justifyContent: 'center', paddingLeft: 100 }}>
                        <Button
                          type="primary"
                          size="large"
                          loading={aMut.isPending}
                          onClick={onGiveActivation}
                          style={{ minWidth: 140, height: 40 }}
                        >
                          发放
                        </Button>
                      </div>
                    </div>
                  </Form>
                </div>
              ),
            },
            {
              key: 'u2',
              label: 'EP铸造回收池',
              children: (
                <div>
                  <div style={{ maxWidth: 900 }}>
                  {isLoading ? null : (
                    <div style={readRowStyle}>
                      <span style={{ width: 200, textAlign: 'right' }}>EP</span>
                      <span style={{ marginLeft: 8, flex: 1, fontWeight: 500 }}>{snap?.ep_to_uoc ?? '—'}</span>
                    </div>
                  )}
                  <Form
                    form={uForm}
                    layout="horizontal"
                    labelAlign="right"
                    requiredMark
                    colon={false}
                    style={{ maxWidth: 900 }}
                    initialValues={{ coin: 'rp' }}
                    labelCol={{ flex: '0 0 200px' }}
                    wrapperCol={{ flex: '1 1 auto' }}
                  >
                    <Form.Item
                      name="coin"
                      label="转换币种"
                      rules={[{ required: true }]}
                      style={{ marginBottom: formItemMb }}
                    >
                      <Select
                        size="large"
                        style={{ maxWidth: '100%' }}
                        options={[
                          { value: 'rp', label: 'RP' },
                          { value: 'tp', label: 'TP' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      name="num"
                      label="发放数量"
                      rules={[{ required: true, message: '请输入发放数量' }]}
                      style={{ marginBottom: formItemMb }}
                    >
                      <Input size="large" placeholder="发放数量" allowClear style={{ maxWidth: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="account"
                      label="用户账号(登录名)"
                      rules={[{ required: true, message: '请输入登录名' }]}
                      style={{ marginBottom: formItemMb }}
                    >
                      <Input size="large" placeholder="用户登录名" autoComplete="off" style={{ maxWidth: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name="remark"
                      label="备注"
                      rules={[{ required: true, message: '请输入备注' }]}
                      style={{ marginBottom: 22 }}
                    >
                      <Input size="large" placeholder="备注" style={{ maxWidth: '100%' }} />
                    </Form.Item>
                    <div style={{ position: 'relative', minHeight: 44, marginTop: 12 }}>
                      <Button
                        type="primary"
                        size="small"
                        style={{ position: 'absolute', left: 0, bottom: 0 }}
                        onClick={() => {
                          void refetchSnap();
                          void qc.invalidateQueries({ queryKey: ['admin-transfer-coin-log'] });
                          msg.info('已刷新数据');
                        }}
                      >
                        刷新页面
                      </Button>
                      <div style={{ display: 'flex', justifyContent: 'center', paddingLeft: 100 }}>
                        <Button
                          type="primary"
                          size="large"
                          loading={uMut.isPending}
                          onClick={onGiveUoc}
                          style={{ minWidth: 140, height: 40 }}
                        >
                          发放
                        </Button>
                      </div>
                    </div>
                  </Form>
                  </div>

                  <div
                    style={{
                      marginTop: 28,
                      paddingTop: 20,
                      borderTop: '1px solid #e8e8e8',
                    }}
                  >
                    <div style={{ marginBottom: 12, fontSize: 15, fontWeight: 600, color: 'rgba(0,0,0,0.88)' }}>
                      转币记录
                    </div>
                    <DataTable<LogRow>
                      queryKey={['admin-transfer-coin-log', 'parity-ep']}
                      endpoint="/api/admin/transfer-coin/log"
                      columns={logColumns}
                      rowKey="id"
                    />
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    </ConfigProvider>
  );
}
